import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'vtwo:inline-flex vtwo:items-center vtwo:justify-center vtwo:gap-2 vtwo:whitespace-nowrap vtwo:rounded-md vtwo:text-sm vtwo:font-medium vtwo:transition-all vtwo:disabled:pointer-events-none vtwo:disabled:opacity-50 vtwo:[&_svg]:pointer-events-none vtwo:[&_svg:not([class*=size-])]:size-4 vtwo:shrink-0 vtwo:[&_svg]:shrink-0 vtwo:outline-none vtwo:focus-visible:border-ring vtwo:focus-visible:ring-ring/50 vtwo:focus-visible:ring-[3px] vtwo:aria-invalid:ring-destructive/20 vtwo:dark:aria-invalid:ring-destructive/40 vtwo:aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default:
          'vtwo:bg-primary vtwo:text-primary-foreground vtwo:shadow-xs vtwo:hover:bg-primary/90',
        destructive:
          'vtwo:bg-destructive vtwo:text-white vtwo:shadow-xs vtwo:hover:bg-destructive/90 vtwo:focus-visible:ring-destructive/20 vtwo:dark:focus-visible:ring-destructive/40 vtwo:dark:bg-destructive/60',
        outline:
          'vtwo:border vtwo:bg-background vtwo:shadow-xs vtwo:hover:bg-accent vtwo:hover:text-accent-foreground vtwo:dark:bg-input/30 vtwo:dark:border-input vtwo:dark:hover:bg-input/50',
        secondary:
          'vtwo:bg-secondary vtwo:text-secondary-foreground vtwo:shadow-xs vtwo:hover:bg-secondary/80',
        ghost:
          'vtwo:hover:bg-accent vtwo:hover:text-accent-foreground vtwo:dark:hover:bg-accent/50',
        link: 'vtwo:text-primary vtwo:underline-offset-4 vtwo:hover:underline',
      },
      size: {
        default: 'vtwo:h-9 vtwo:px-4 vtwo:py-2 vtwo:has-[>svg]:px-3',
        sm: 'vtwo:h-8 vtwo:rounded-md vtwo:gap-1.5 vtwo:px-3 vtwo:has-[>svg]:px-2.5',
        lg: 'vtwo:h-10 vtwo:rounded-md vtwo:px-6 vtwo:has-[>svg]:px-4',
        xl: 'vtwo:h-12 vtwo:rounded-md vtwo:px-8 vtwo:text-base vtwo:font-semibold vtwo:has-[>svg]:px-5',
        icon: 'vtwo:size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
